"use client";

import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import SearchInput from "./SearchInput";
import SelectDropdown from "./SelectDropdown";
import VagaCard from "./VagaCard";
import OrderSelect from "./OrderSelect";
import Pagination from "./Pagination";
import JobsEmptyState from "./JobsEmptyState";
import FutureJobsHeading from "./FutureJobsHeading";
import ResetAllJobsButton from "./ResetAllJobsButton";
import type {
  Vaga,
  FiltroTipoVaga,
  OrdenacaoVagas,
  DepartamentoFlat,
  DepartamentoHierarquico,
  LocalizacaoHierarquica,
} from "../../types/vaga";
import { fetchVagas } from "../../services/vagas";

const ITEMS_PER_PAGE = 10;
const VAGAS_OCULTAS = new Set(["5200480008", "5216535008", "5399790008"]);
type DepartamentoOption = {
  id: string;
  label: string;
  children?: DepartamentoOption[];
};
const EMPTY_OPEN_JOBS_TITLE =
  "Nenhuma vaga aberta encontrada com os filtros selecionados.";
const EMPTY_OPEN_JOBS_DESCRIPTION =
  "Tente ajustar seus critérios de busca ou veja todas as vagas disponíveis.";
const TIPO_VAGA_OPTIONS = [
  { id: "todas", label: "Todas" },
  { id: "abertas", label: "Vagas abertas" },
  { id: "futuras", label: "Vagas futuras" },
];

const parseLocationForFilter = (
  name?: string,
): { cidade?: string; estado: string } | null => {
  if (!name) return null;
  if (name.includes(";")) return null;

  const parts = name
    .split(", ")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 3 && parts[2] === "Brasil") {
    return { cidade: parts[0], estado: parts[1] };
  }
  if (parts.length === 2 && parts[1] === "Brasil") {
    return { estado: parts[0] };
  }
  return null;
};

const getAllDepartmentIds = (
  deps: DepartamentoFlat[],
  parentId: string,
): string[] => {
  let ids = [parentId];
  const children = deps.filter((dep) => dep.parentId === parentId);
  for (const child of children) {
    ids = ids.concat(getAllDepartmentIds(deps, child.id));
  }
  return ids;
};

const mapDepartamentoOptions = (
  deps: DepartamentoHierarquico[],
): DepartamentoOption[] => {
  return deps.map((dep) => ({
    id: dep.id,
    label: dep.nome,
    children: dep.subdepartamentos
      ? mapDepartamentoOptions(dep.subdepartamentos)
      : undefined,
  }));
};

const sortVagasByOrder = (lista: Vaga[], ordenacao: OrdenacaoVagas) => {
  const resultado = [...lista];

  switch (ordenacao) {
    case "recente":
      resultado.sort(
        (a, b) => b.dataAtualizacao.getTime() - a.dataAtualizacao.getTime(),
      );
      break;
    case "az":
      resultado.sort((a, b) => a.titulo.localeCompare(b.titulo));
      break;
    case "za":
      resultado.sort((a, b) => b.titulo.localeCompare(a.titulo));
      break;
  }

  return resultado;
};

const normalizeSearchText = (value?: string | null) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const rankFutureJobsBySearchTerms = (
  vagas: Vaga[],
  busca: string,
  departamentosFlat: DepartamentoFlat[],
) => {
  const termos = Array.from(
    new Set(
      normalizeSearchText(busca)
        .split(/\s+/)
        .map((termo) => termo.trim())
        .filter(Boolean),
    ),
  );

  if (termos.length === 0) {
    return vagas;
  }

  return vagas
    .map((vaga, index) => {
      const departamentos = vaga.departamentoIds
        .map((depId) => departamentosFlat.find((dep) => dep.id === depId)?.nome)
        .filter(Boolean)
        .join(" ");
      const searchableContent = normalizeSearchText(
        [
          vaga.titulo,
          vaga.requisitos,
          vaga.locationName,
          vaga.localizacao.cidade,
          vaga.localizacao.estado,
          departamentos,
        ].join(" "),
      );
      const score = termos.reduce((total, termo) => {
        return searchableContent.includes(termo) ? total + 1 : total;
      }, 0);

      return { vaga, index, score };
    })
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return a.index - b.index;
    })
    .map(({ vaga }) => vaga);
};

export default function VagasList() {
  const searchParams = useSearchParams();

  // State
  const [vagas, setVagas] = useState<Vaga[]>([]);
  const [departamentosData, setDepartamentosData] = useState<
    DepartamentoHierarquico[]
  >([]);
  const [departamentosFlat, setDepartamentosFlat] = useState<
    DepartamentoFlat[]
  >([]);
  const [localizacoesData, setLocalizacoesData] = useState<
    LocalizacaoHierarquica[]
  >([]);
  const [localizacoesBase, setLocalizacoesBase] = useState<
    LocalizacaoHierarquica[]
  >([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [busca, setBusca] = useState("");
  const [localizacoes, setLocalizacoes] = useState<string[]>([]);
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [tiposVaga, setTiposVaga] = useState<FiltroTipoVaga[]>([]);
  const [ordenacao, setOrdenacao] = useState<OrdenacaoVagas>("recente");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [departamentosJaExpandidos, setDepartamentosJaExpandidos] =
    useState(false);
  const [prefiltroAreaAtivo, setPrefiltroAreaAtivo] = useState(false);

  useEffect(() => {
    let ativo = true;

    async function carregarVagas() {
      try {
        setCarregando(true);
        setErro(null);
        const resultado = await fetchVagas();
        if (!ativo) return;
        setVagas(resultado.vagas.filter((v) => !VAGAS_OCULTAS.has(v.idOriginal ?? "")));
        setDepartamentosData(resultado.departamentos);
        setDepartamentosFlat(resultado.departamentosFlat);
        setLocalizacoesData(resultado.localizacoes);
        setLocalizacoesBase(resultado.localizacoes);
      } catch (error) {
        if (!ativo) return;
        setErro(
          error instanceof Error ? error.message : "Falha ao carregar vagas.",
        );
      } finally {
        if (ativo) {
          setCarregando(false);
        }
      }
    }

    carregarVagas();
    return () => {
      ativo = false;
    };
  }, []);

  // Handle URL params for pre-filtering
  useEffect(() => {
    const areaParam = searchParams.get("area");
    if (areaParam && departamentosFlat.length > 0) {
      setPrefiltroAreaAtivo(true);
      const area = areaParam.toLowerCase();
      if (area.includes("corporativo")) {
        const departamentosCorporativos = departamentosFlat.filter((dep) => {
          const nome = dep.nome.toLowerCase();
          return !nome.includes("vendas") && !nome.includes("tecnologia");
        });

        let validDepartmentIds: string[] = [];
        departamentosCorporativos.forEach((dep) => {
          validDepartmentIds = validDepartmentIds.concat(
            getAllDepartmentIds(departamentosFlat, dep.id),
          );
        });

        const departamentosExcluir = departamentosFlat.filter((dep) => {
          const nome = dep.nome.toLowerCase();
          return nome.includes("vendas") || nome.includes("tecnologia");
        });

        let idsParaExcluir: string[] = [];
        departamentosExcluir.forEach((dep) => {
          idsParaExcluir = idsParaExcluir.concat(
            getAllDepartmentIds(departamentosFlat, dep.id),
          );
        });

        validDepartmentIds = validDepartmentIds.filter(
          (id) => !idsParaExcluir.includes(id),
        );

        setDepartamentosJaExpandidos(true);
        setDepartamentos(validDepartmentIds);
      } else {
        let termoBusca = "";
        if (area.includes("comercial")) {
          termoBusca = "Vendas";
        } else if (area.includes("tecnologia")) {
          termoBusca = "Tecnologia";
        } else {
          termoBusca = "";
        }

        if (termoBusca) {
          const departamentoEncontrado = departamentosFlat.find((dep) =>
            dep.nome.toLowerCase() === termoBusca.toLowerCase(),
          );

          if (departamentoEncontrado) {
            setDepartamentosJaExpandidos(false);
            setDepartamentos([departamentoEncontrado.id]);
          }
        }
      }
    } else {
      setPrefiltroAreaAtivo(false);
    }

    const buscaParam = searchParams.get("busca");
    if (buscaParam) {
      setBusca(buscaParam);
    }
  }, [searchParams, departamentosFlat]);

  // Convert mock data to dropdown options
  const localizacaoOptions = localizacoesData.map((loc) => ({
    id: loc.estado,
    label: loc.estado,
    children: loc.cidades.map((cidade) => ({
      id: `${loc.estado}-${cidade}`,
      label: cidade,
    })),
  }));

  const departamentoOptions = mapDepartamentoOptions(departamentosData);

  const vagasAgrupadas = useMemo(() => {
    let resultado = [...vagas];
    const tipoSelecionado = tiposVaga[0];

    if (tipoSelecionado) {
      resultado = resultado.filter(
        (vaga) => (vaga.tipo ?? "abertas") === tipoSelecionado,
      );
    }

    // Filter by search term
    if (busca.trim()) {
      const termoBusca = busca.toLowerCase().trim();
      resultado = resultado.filter((vaga) => {
        const tituloMatch = vaga.titulo.toLowerCase().includes(termoBusca);

        const departamentoMatch = vaga.departamentoIds.some((depId) => {
          const dep = departamentosFlat.find((d) => d.id === depId);
          return dep?.nome.toLowerCase().includes(termoBusca);
        });

        const localidadeMatch =
          vaga.localizacao.cidade?.toLowerCase().includes(termoBusca) ||
          vaga.localizacao.estado?.toLowerCase().includes(termoBusca) ||
          vaga.locationName?.toLowerCase().includes(termoBusca);

        const conteudoMatch =
          vaga.requisitos?.toLowerCase().includes(termoBusca) ?? false;

        return (
          tituloMatch || departamentoMatch || localidadeMatch || conteudoMatch
        );
      });
    }

    // Filter by location
    if (localizacoes.length > 0) {
      resultado = resultado.filter((vaga) => {
        const estadoMatch = localizacoes.includes(vaga.localizacao.estado);
        const cidadeMatch = localizacoes.includes(
          `${vaga.localizacao.estado}-${vaga.localizacao.cidade}`,
        );
        return estadoMatch || cidadeMatch;
      });
    }

    // Filter by department
    if (departamentos.length > 0) {
      let validDepartmentIds: string[] = [];
      if (departamentosJaExpandidos) {
        validDepartmentIds = [...departamentos];
      } else {
        departamentos.forEach((depId) => {
          validDepartmentIds = validDepartmentIds.concat(
            getAllDepartmentIds(departamentosFlat, depId),
          );
        });
      }

      resultado = resultado.filter((vaga) => {
        return vaga.departamentoIds.some((depId) =>
          validDepartmentIds.includes(depId),
        );
      });
    }

    const vagasAbertas = sortVagasByOrder(
      resultado.filter((vaga) => (vaga.tipo ?? "abertas") === "abertas"),
      ordenacao,
    );
    const vagasFuturas = sortVagasByOrder(
      resultado.filter((vaga) => vaga.tipo === "futuras"),
      ordenacao,
    );

    return {
      vagasAbertas,
      vagasFuturas,
      todas: [...vagasAbertas, ...vagasFuturas],
    };
  }, [
    vagas,
    busca,
    localizacoes,
    departamentos,
    tiposVaga,
    ordenacao,
    departamentosFlat,
    departamentosJaExpandidos,
  ]);

  const vagasFuturasOriginais = useMemo(() => {
    return sortVagasByOrder(
      vagas.filter((vaga) => vaga.tipo === "futuras"),
      ordenacao,
    );
  }, [vagas, ordenacao]);
  const vagasFuturasFallback = useMemo(() => {
    if (!busca.trim()) {
      return vagasFuturasOriginais;
    }

    return rankFutureJobsBySearchTerms(
      vagasFuturasOriginais,
      busca,
      departamentosFlat,
    );
  }, [vagasFuturasOriginais, busca, departamentosFlat]);

  const temFiltrosAplicadosNaLista =
    busca.trim() !== "" ||
    localizacoes.length > 0 ||
    departamentos.length > 0 ||
    tiposVaga.length > 0;
  const tipoVagaSelecionado = tiposVaga[0];
  const exibirTodasFuturasNaPaginacaoPadrao =
    temFiltrosAplicadosNaLista &&
    tipoVagaSelecionado !== "futuras" &&
    tipoVagaSelecionado !== "abertas" &&
    vagasAgrupadas.vagasAbertas.length > 0 &&
    vagasAgrupadas.vagasFuturas.length === 0 &&
    vagasFuturasOriginais.length > 0;
  const vagasParaPaginacao =
    tipoVagaSelecionado === "abertas"
      ? vagasAgrupadas.vagasAbertas
      : tipoVagaSelecionado === "futuras"
        ? vagasAgrupadas.vagasFuturas
        : exibirTodasFuturasNaPaginacaoPadrao
          ? [...vagasAgrupadas.vagasAbertas, ...vagasFuturasOriginais]
          : vagasAgrupadas.todas;

  // Pagination
  const totalFiltrado = vagasParaPaginacao.length;
  const totalPages = Math.ceil(totalFiltrado / ITEMS_PER_PAGE);
  const vagasPaginadas = vagasParaPaginacao.slice(
    (paginaAtual - 1) * ITEMS_PER_PAGE,
    paginaAtual * ITEMS_PER_PAGE,
  );
  const vagasAbertasPaginadas = vagasPaginadas.filter(
    (vaga) => (vaga.tipo ?? "abertas") === "abertas",
  );
  const vagasFuturasPaginadas = vagasPaginadas.filter(
    (vaga) => vaga.tipo === "futuras",
  );
  const mostrarFallbackSemResultados =
    !carregando && !erro && vagasPaginadas.length === 0;
  const usarFallbackFuturasOriginais =
    mostrarFallbackSemResultados && tipoVagaSelecionado !== "abertas";
  const totalPagesFallback = Math.ceil(
    vagasFuturasFallback.length / ITEMS_PER_PAGE,
  );
  const vagasFuturasOriginaisPaginadas = vagasFuturasFallback.slice(
    (paginaAtual - 1) * ITEMS_PER_PAGE,
    paginaAtual * ITEMS_PER_PAGE,
  );
  const totalExibido = mostrarFallbackSemResultados
    ? usarFallbackFuturasOriginais
      ? vagasFuturasOriginaisPaginadas.length
      : 0
    : vagasPaginadas.length;
  const totalDisponivel = mostrarFallbackSemResultados
    ? usarFallbackFuturasOriginais
      ? vagasFuturasFallback.length
      : 0
    : totalFiltrado;
  const shouldShowPaginatedOpenEmptyState =
    vagasAbertasPaginadas.length === 0 &&
    paginaAtual === 1 &&
    tipoVagaSelecionado !== "futuras";
  const shouldShowPaginatedFutureHeading =
    vagasFuturasPaginadas.length > 0 &&
    tipoVagaSelecionado !== "futuras" &&
    (vagasAbertasPaginadas.length > 0 ||
      (vagasAbertasPaginadas.length === 0 && paginaAtual === 1));
  const paginatedFutureHeadingVariant =
    vagasAbertasPaginadas.length === 0 ? "fallback" : "inline";
  const shouldShowFallbackOpenEmptyState = tipoVagaSelecionado !== "futuras";
  const shouldShowFallbackFutureHeading =
    tiposVaga.length === 0 && paginaAtual === 1;
  const shouldRenderFallbackFutureJobs = tipoVagaSelecionado !== "abertas";
  const shouldShowPagination =
    totalPages > 1 || (usarFallbackFuturasOriginais && totalPagesFallback > 1);
  const paginationTotalPages = usarFallbackFuturasOriginais
    ? totalPagesFallback
    : totalPages;

  // Reset page when filters change
  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, localizacoes, departamentos, tiposVaga, ordenacao]);

  useEffect(() => {
    if (departamentosFlat.length === 0) return;
    if (!prefiltroAreaAtivo) {
      setLocalizacoesData(localizacoesBase);
      return;
    }

    let vagasParaLocalizacao = [...vagas];
    const tipoSelecionado = tiposVaga[0];

    if (tipoSelecionado) {
      vagasParaLocalizacao = vagasParaLocalizacao.filter(
        (vaga) => (vaga.tipo ?? "abertas") === tipoSelecionado,
      );
    }

    if (departamentos.length > 0) {
      let validDepartmentIds: string[] = [];
      if (departamentosJaExpandidos) {
        validDepartmentIds = [...departamentos];
      } else {
        departamentos.forEach((depId) => {
          validDepartmentIds = validDepartmentIds.concat(
            getAllDepartmentIds(departamentosFlat, depId),
          );
        });
      }

      vagasParaLocalizacao = vagasParaLocalizacao.filter((vaga) =>
        vaga.departamentoIds.some((depId) =>
          validDepartmentIds.includes(depId),
        ),
      );
    }

    const localizacoesMap = new Map<string, Set<string>>();
    vagasParaLocalizacao.forEach((vaga) => {
      const parsed = parseLocationForFilter(vaga.locationName);
      if (!parsed) return;
      if (!localizacoesMap.has(parsed.estado)) {
        localizacoesMap.set(parsed.estado, new Set());
      }
      if (parsed.cidade) {
        localizacoesMap.get(parsed.estado)?.add(parsed.cidade);
      }
    });

    const localizacoesAtualizadas = Array.from(localizacoesMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([estado, cidades]) => ({
        estado,
        cidades: Array.from(cidades).sort((a, b) => a.localeCompare(b)),
      }));

    setLocalizacoesData(localizacoesAtualizadas);
  }, [
    departamentos,
    departamentosJaExpandidos,
    vagas,
    departamentosFlat,
    prefiltroAreaAtivo,
    localizacoesBase,
    tiposVaga,
  ]);

  const limparFiltros = () => {
    setBusca("");
    setLocalizacoes([]);
    setDepartamentos([]);
    setTiposVaga([]);
    setDepartamentosJaExpandidos(false);
    setPrefiltroAreaAtivo(false);
    setOrdenacao("recente");
    setPaginaAtual(1);
    setLocalizacoesData(localizacoesBase);

    // Limpa os query params da URL
    const url = new URL(window.location.href);
    url.searchParams.delete("busca");
    url.searchParams.delete("area");
    window.history.replaceState({}, "", url.pathname);
  };

  const temFiltrosAtivos = temFiltrosAplicadosNaLista;

  return (
    <section className="py-8 lg:py-12 bg-background-default">
      <div className="container-default">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-[30px]">
          {/* Filters Column */}
          <aside className="w-full lg:w-[380px] shrink-0">
            <div className="flex flex-col gap-5">
              <SearchInput value={busca} onChange={setBusca} />

              <SelectDropdown
                label="Localização"
                options={localizacaoOptions}
                selectedValues={localizacoes}
                onChange={setLocalizacoes}
                placeholder="Localidade"
              />

              <SelectDropdown
                label="Departamento"
                options={departamentoOptions}
                selectedValues={departamentos}
                onChange={(values) => {
                  setDepartamentos(values);
                  setDepartamentosJaExpandidos(false);
                }}
                placeholder="Selecione"
              />

              <SelectDropdown
                label="Tipo de vaga"
                options={TIPO_VAGA_OPTIONS}
                selectedValues={tiposVaga}
                onChange={(values) => {
                  const valorSelecionado = values[0] as
                    | FiltroTipoVaga
                    | undefined;
                  if (!valorSelecionado || valorSelecionado === "todas") {
                    setTiposVaga([]);
                    return;
                  }
                  setTiposVaga([valorSelecionado]);
                }}
                placeholder="Todas"
                singleSelect
                clearOptionId="todas"
              />

              {temFiltrosAtivos && (
                <button
                  type="button"
                  onClick={limparFiltros}
                  className="self-start text-sm font-semibold text-blue-main rounded-full px-3 py-1 hover:bg-blue-main/10 transition-colors cursor-pointer"
                >
                  Limpar filtros
                </button>
              )}
            </div>
          </aside>

          {/* Results Column */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-0">
              {/* Header with count and order */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <p className="text-base text-text-secondary">
                  Exibindo {totalExibido} de {totalDisponivel}
                </p>
                <OrderSelect value={ordenacao} onChange={setOrdenacao} />
              </div>

              {/* Job list */}
              <div className="flex flex-col gap-2 mt-5">
                {carregando ? (
                  <div className="text-center py-12 text-text-secondary">
                    Carregando vagas...
                  </div>
                ) : erro ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <p className="text-text-secondary text-lg">{erro}</p>
                    <ResetAllJobsButton onClick={limparFiltros} className="mt-6" />
                  </div>
                ) : vagasPaginadas.length > 0 ? (
                  <>
                    {vagasAbertasPaginadas.map((vaga) => (
                      <VagaCard key={vaga.id} vaga={vaga} />
                    ))}

                    {shouldShowPaginatedOpenEmptyState && (
                      <JobsEmptyState
                        title={EMPTY_OPEN_JOBS_TITLE}
                        description={EMPTY_OPEN_JOBS_DESCRIPTION}
                        onReset={limparFiltros}
                      />
                    )}

                    {shouldShowPaginatedFutureHeading && (
                      <FutureJobsHeading
                        variant={paginatedFutureHeadingVariant}
                        centered={vagasAbertasPaginadas.length === 0}
                      />
                    )}

                    {vagasFuturasPaginadas.map((vaga) => (
                      <VagaCard key={vaga.id} vaga={vaga} />
                    ))}
                  </>
                ) : (
                  <>
                    {shouldShowFallbackOpenEmptyState && (
                      <JobsEmptyState
                        title={EMPTY_OPEN_JOBS_TITLE}
                        description={EMPTY_OPEN_JOBS_DESCRIPTION}
                        onReset={limparFiltros}
                      />
                    )}

                    {vagasFuturasOriginaisPaginadas.length > 0 && (
                      <>
                        {shouldShowFallbackFutureHeading && (
                          <FutureJobsHeading variant="fallback" centered />
                        )}

                        {shouldRenderFallbackFutureJobs &&
                          vagasFuturasOriginaisPaginadas.map((vaga) => (
                            <VagaCard key={vaga.id} vaga={vaga} />
                          ))}
                      </>
                    )}
                  </>
                )}
              </div>

              {/* Pagination */}
              {shouldShowPagination && (
                <div className="flex justify-center mt-5">
                  <Pagination
                    currentPage={paginaAtual}
                    totalPages={paginationTotalPages}
                    onPageChange={setPaginaAtual}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
